import { ActivityLogWithUsers } from '../../commonTypes/ActivityLogTypes/activityLog.types';
import {
    AddProjectAssigneesActivityLog,
    RemoveProjectAssigneeActivityLog,
    ProjectCreationActivityLog,
    ProjectDeletionActivityLog,
    ProjectEditActivityLog,
    ProjectNoteCreationActivityLog,
    ProjectMeetCreationActivityLog,
    ProjectUpdateAssigneeActivityLog,
} from '../../commonTypes/ActivityLogTypes/projectService.types';
import {
    SubmissionIntervalCreationActivityLog,
    SubmissionIntervalDeletionActivityLog,
} from '../../commonTypes/ActivityLogTypes/timelogService.types';
import { TranslationFormatter } from './TranslationFormatter';

export class ProjectAuditTrailMessageFormatter {
    private formatter: TranslationFormatter;

    constructor(formatter: TranslationFormatter) {
        this.formatter = formatter;
    }

    private getFullName(givenName?: string, familyName?: string): string {
        if (!givenName && !familyName) return '';
        if (!givenName) return familyName || '';
        if (!familyName) return givenName;
        return `${givenName} ${familyName}`;
    }

    private formatFieldName(fieldName: string): string {
        return fieldName
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, str => str.toUpperCase())
            .trim();
    }

    private formatWithInitiator(message: string, initiatorUser?: any): string {
        const initiatorName = this.getFullName(initiatorUser?.givenName, initiatorUser?.familyName);
        if (initiatorName) {
            return message + this.formatter.getTranslation('projectAuditTrail.byInitiator', { initiatorName });
        }
        return `${message}.`;
    }

    public formatProjectCreation(log: ProjectCreationActivityLog): string {
        const { projectName, clientName } = log.activityLogDetails;
        const baseMessage = this.formatter.getTranslation('projectAuditTrail.projectCreation', {
            projectName: projectName || 'Project',
            clientName: clientName || 'Client',
        });
        return this.formatWithInitiator(baseMessage, log.initiatorUser);
    }

    public formatProjectEdit(log: ProjectEditActivityLog): string {
        const { projectName, editedFields } = log.activityLogDetails;
        const fields = editedFields?.map(field => this.formatFieldName(field)).join(', ') || 'fields';
        const baseMessage = this.formatter.getTranslation('projectAuditTrail.projectEdit', {
            projectName: projectName || 'Project',
            fields,
        });
        return this.formatWithInitiator(baseMessage, log.initiatorUser);
    }

    public formatProjectDeletion(log: ProjectDeletionActivityLog): string {
        const { projectName, clientName } = log.activityLogDetails;
        const baseMessage = this.formatter.getTranslation('projectAuditTrail.projectDeletion', {
            projectName: projectName || 'Project',
            clientName: clientName || 'Client',
        });
        return this.formatWithInitiator(baseMessage, log.initiatorUser);
    }

    public formatProjectAddAssignees(log: AddProjectAssigneesActivityLog): string {
        const { addedUsers, projectName, roleType } = log.activityLogDetails;
        const addedNames =
            addedUsers
                ?.map(user => this.getFullName(user?.givenName, user?.familyName))
                .filter(Boolean)
                .join(', ') || 'assignees';
        const baseMessage = this.formatter.getTranslation('projectAuditTrail.assigneesAdded', {
            addedNames,
            projectName: projectName || 'project',
            roleType: roleType || 'member',
        });
        return this.formatWithInitiator(baseMessage, log.initiatorUser);
    }

    public formatProjectRemoveAssignee(log: RemoveProjectAssigneeActivityLog): string {
        const { removedAssignee, projectName } = log.activityLogDetails;
        const removedName = this.getFullName(removedAssignee?.givenName, removedAssignee?.familyName) || 'assignee';
        const baseMessage = this.formatter.getTranslation('projectAuditTrail.assigneeRemoved', {
            removedName,
            projectName: projectName || 'project',
        });
        return this.formatWithInitiator(baseMessage, log.initiatorUser);
    }

    public formatProjectUpdateAssignee(log: ProjectUpdateAssigneeActivityLog): string {
        const { assigneeName, oldRate, newRate, rate, billable, rateType, employmentType, updatedAssignee } = log.activityLogDetails;

        const affectedUser = updatedAssignee ?? log.affectedUsers?.[0]?.affectedUser ?? null;
        const affectedUserName = affectedUser ? this.getFullName(affectedUser.givenName, affectedUser.familyName) : assigneeName || 'Assignee';
        const initiatorName = this.getFullName(log.initiatorUser?.givenName, log.initiatorUser?.familyName);

        const prefix = initiatorName
            ? this.formatter.getTranslation('projectAuditTrail.assigneeUpdated.withInitiator', {
                  initiatorName,
                  affectedUserName,
              })
            : this.formatter.getTranslation('projectAuditTrail.assigneeUpdated.withoutInitiator', {
                  affectedUserName,
              });

        if (oldRate !== undefined && newRate !== undefined) {
            return (
                prefix +
                this.formatter.getTranslation('projectAuditTrail.assigneeUpdated.rateChange', {
                    oldRate,
                    newRate,
                }) +
                '.'
            );
        }

        if (rate !== undefined || employmentType || billable !== undefined) {
            if (billable === false) {
                return prefix + this.formatter.getTranslation('projectAuditTrail.assigneeUpdated.nonBillable') + '.';
            }

            const changes: string[] = [];
            if (rate !== undefined) {
                changes.push(rateType === 'HOURLY' ? `$${rate}/hr` : `$${rate}`);
            }
            if (employmentType) {
                changes.push(employmentType);
            }
            if (billable === true) {
                changes.push('Billable');
            }

            return prefix + this.formatter.getTranslation('projectAuditTrail.assigneeUpdated.changes', { changes: changes.join(', ') }) + '.';
        }

        return initiatorName ? `${prefix}.` : prefix + this.formatter.getTranslation('projectAuditTrail.assigneeUpdated.infoUpdated') + '.';
    }

    public formatProjectNoteCreation(log: ProjectNoteCreationActivityLog): string {
        const { noteTitle } = log.activityLogDetails;
        const baseMessage = this.formatter.getTranslation('projectAuditTrail.noteCreated', {
            noteTitle: noteTitle || 'Note',
        });
        return this.formatWithInitiator(baseMessage, log.initiatorUser);
    }

    public formatProjectMeetCreation(log: ProjectMeetCreationActivityLog): string {
        const { meetTitle } = log.activityLogDetails;
        const baseMessage = this.formatter.getTranslation('projectAuditTrail.meetingCreated', {
            meetTitle: meetTitle || 'Meeting',
        });
        return this.formatWithInitiator(baseMessage, log.initiatorUser);
    }

    public formatSubmissionIntervalCreation(log: SubmissionIntervalCreationActivityLog): string {
        const { month } = log.activityLogDetails;
        const baseMessage = this.formatter.getTranslation('projectAuditTrail.submissionIntervalCreated', {
            month,
        });
        return this.formatWithInitiator(baseMessage, log.initiatorUser);
    }

    public formatSubmissionIntervalDeletion(log: SubmissionIntervalDeletionActivityLog): string {
        const { startDate, endDate } = log.activityLogDetails;
        const baseMessage = this.formatter.getTranslation('projectAuditTrail.submissionIntervalDeleted', {
            startDate,
            endDate,
        });
        return this.formatWithInitiator(baseMessage, log.initiatorUser);
    }

    public generateMessage(log: ActivityLogWithUsers): string {
        try {
            switch (log.activityLogType) {
                case 'PROJECT_CREATION':
                    return this.formatProjectCreation(log as ProjectCreationActivityLog);
                case 'PROJECT_EDIT':
                    return this.formatProjectEdit(log as ProjectEditActivityLog);
                case 'PROJECT_ADD_ASSIGNEES':
                    return this.formatProjectAddAssignees(log as AddProjectAssigneesActivityLog);
                case 'PROJECT_REMOVE_ASSIGNEE':
                    return this.formatProjectRemoveAssignee(log as RemoveProjectAssigneeActivityLog);
                case 'PROJECT_UPDATE_ASSIGNEE':
                    return this.formatProjectUpdateAssignee(log as ProjectUpdateAssigneeActivityLog);
                case 'PROJECT_NOTE_CREATION':
                    return this.formatProjectNoteCreation(log as ProjectNoteCreationActivityLog);
                case 'PROJECT_MEET_CREATION':
                    return this.formatProjectMeetCreation(log as ProjectMeetCreationActivityLog);
                case 'SUBMISSION_INTERVAL_CREATION':
                    return this.formatSubmissionIntervalCreation(log as SubmissionIntervalCreationActivityLog);
                case 'SUBMISSION_INTERVAL_DELETION':
                    return this.formatSubmissionIntervalDeletion(log as SubmissionIntervalDeletionActivityLog);
                default:
                    return this.formatter.getTranslation('projectAuditTrail.defaultMessage', {
                        activityType: log.activityLogType.replace(/_/g, ' ').toLowerCase(),
                    });
            }
        } catch (error) {
            console.error('Error formatting activity log:', error, log);
            return this.formatter.getTranslation('projectAuditTrail.defaultMessage', {
                activityType: log.activityLogType.replace(/_/g, ' ').toLowerCase(),
            });
        }
    }
}
